import { Tag, Tooltip } from 'antd';
import { formatDateTime } from '@/utils/datetime';
import StatusIndicator from '@/components/genealogy/StatusIndicator';

// ==============================|| BATCH PROCESS DETAILS - SHARED HELPERS ||============================== //

export const fmt = (v) => formatDateTime(v);

export const yesNo = (v) =>
  v === true ? (
    <Tag color="green">Yes</Tag>
  ) : v === false ? (
    <Tag color="red">No</Tag>
  ) : (
    <Tag>—</Tag>
  );

/**
 * The server's status words → a StatusIndicator state. Exact, case-insensitive matches only: the old
 * substring test turned "Review & Not Approved" and "Incomplete" green. A word not listed here is
 * shown as the server wrote it, in the neutral state — never as a pass.
 *
 * Vocabularies (verified 2 Oct 2026):
 *   Batch_Size_Info.batch_status · process execution_status (batch/process-list) ·
 *   Approval_Status.name (recipe / operation approvals) · Batch_Approval ("Approved"/"Pending",
 *   composed by the server) · OperationSupervisorApproval.status · Sample_Status.name ·
 *   Sample_Details.sampling_result.
 */
const STATUS_STATE = {
  // batch / process / operation execution
  pending: 'pending',
  scheduled: 'pending',
  'not started': 'pending',
  running: 'active',
  'in progress': 'active',
  hold: 'hold',
  completed: 'ok',
  released: 'ok',
  terminated: 'error',
  cancelled: 'neutral',
  rejected: 'error',
  // approvals
  'under review': 'pending',
  reviewed: 'info',
  'review & approved': 'ok',
  'review & not approved': 'error',
  approved: 'ok',
  // samples
  'sent to qc': 'pending',
  resampling: 'pending',
  'under test': 'active',
  invalidated: 'warning',
  pass: 'ok',
  fail: 'error',
};

export const statusState = (status) => STATUS_STATE[String(status ?? '').trim().toLowerCase()] || 'neutral';

/** A server status rendered with the shared indicator; the label is always the server's own word. */
export const StatusBadge = ({ status, tooltip }) =>
  status ? <StatusIndicator state={statusState(status)} label={status} tooltip={tooltip} /> : '—';

export const priorityTagColor = (priority = '') => {
  const v = String(priority).toLowerCase();
  if (v === 'high' || v === 'critical') return 'red';
  if (v === 'medium') return 'orange';
  if (v === 'low') return 'blue';
  return 'default';
};

export const personName = (person) =>
  person ? `${person.name || '—'}${person.designation ? ` (${person.designation})` : ''}` : '—';

export const formatSeconds = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const s = Number(seconds);
  if (Number.isNaN(s)) return '—';
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem}s` : `${rem}s`;
};

export const TicketTag = ({ ticket }) => {
  if (!ticket) return '—';
  return (
    <Tooltip
      title={
        <div>
          <div>{ticket.desc || 'No description'}</div>
          <div style={{ marginTop: 4, opacity: 0.85 }}>
            Raised by {personName(ticket.created_by)} on {fmt(ticket.created_at)}
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-1" style={{ cursor: 'help' }}>
        <span style={{ fontSize: 12 }}>
          <Tag color={priorityTagColor(ticket.priority)}>{ticket.priority || '—'}</Tag>
          <b>{ticket.unique_id || ticket.id}</b>
        </span>
        <span style={{ fontSize: 12 }}>{ticket.title}</span>
        <span style={{ fontSize: 11, color: '#64748b' }}>
          {ticket.current_stage}
          {ticket.is_completed ? ' · Completed' : ' · Open'}
        </span>
      </div>
    </Tooltip>
  );
};
